import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { LogoutService, LogoutReason } from './logout.service';
import { BroadcastChannelService } from '../broadcast-channel.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { AuthExchangeResponse } from './auth-exchange.model';
import {
  ACCESS_TOKEN_KEY,
  AUTH_EXCHANGE_PATH,
  AUTH_REFRESH_PATH,
  INACTIVITY_TIMEOUT_MS,
  REFRESH_TOKEN_KEY
} from './auth.constants';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private checkInterval: any;
  private loginCompleted = new BehaviorSubject<boolean>(false);
  loginCompleted$ = this.loginCompleted.asObservable();
  userEmail: any;
  #azureIdToken: string | null = null;
  #loginInProgress = false;
  #refreshInFlight$: Observable<boolean> | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private logoutService: LogoutService,
    private broadcastChannelService: BroadcastChannelService
  ) {
    this.startMonitoring();
  }

  login(userEmail: any, azureIdToken?: string) {
    if (this.#loginInProgress) {
      return;
    }

    this.userEmail = userEmail;

    if (!azureIdToken) {
      this.loginCompleted.next(false);
      return;
    }

    this.#loginInProgress = true;
    this.#exchangeToken(azureIdToken);
  }

  setAzureIdToken(idToken: string): void {
    this.#azureIdToken = idToken;
  }

  getAzureIdToken(): string | null {
    return this.#azureIdToken;
  }

  clearAzureIdToken(): void {
    this.#azureIdToken = null;
  }

  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  restoreSessionIfValid(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }

    if (!this.loginCompleted.getValue()) {
      this.loginCompleted.next(true);
    }

    return true;
  }

  refreshTokens(): Observable<boolean> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      return of(false);
    }

    if (this.#refreshInFlight$) {
      return this.#refreshInFlight$;
    }

    const url = `${environment.urlProfileManagement}${AUTH_REFRESH_PATH}`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${refreshToken}`,
      byPass: 'true'
    });

    this.#refreshInFlight$ = this.http.post<AuthExchangeResponse>(url, {}, { headers }).pipe(
      map((response) => {
        this.#persistTokens(response);
        return true;
      }),
      catchError(() => of(false)),
      finalize(() => {
        this.#refreshInFlight$ = null;
      }),
      shareReplay(1)
    );

    return this.#refreshInFlight$;
  }

  #exchangeToken(azureIdToken: string): void {
    const url = `${environment.urlProfileManagement}${AUTH_EXCHANGE_PATH}`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${azureIdToken}`,
      byPass: 'true'
    });

    this.http.post<AuthExchangeResponse>(url, {}, { headers }).subscribe({
      next: (response) => {
        this.#handleTokenSuccess(response);
      },
      error: () => {
        this.#loginInProgress = false;
        this.loginCompleted.next(false);
        this.logout('token_expired');
      }
    });
  }

  #handleTokenSuccess(response: AuthExchangeResponse): void {
    this.#persistTokens(response);
    this.#loginInProgress = false;
    this.loginCompleted.next(true);
  }

  #persistTokens(response: AuthExchangeResponse): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
  }

  private clearInterval() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  }

  logout(reason: LogoutReason = 'manual', skipBroadcast: boolean = false) {
    const currentUrl = this.router.url || window.location.pathname + window.location.search + window.location.hash;

    if (!skipBroadcast) {
      if (reason !== 'manual') {
        try {
          this.logoutService.setReturnUrl(currentUrl);
          this.broadcastChannelService.notifyLogoutInitiated(reason, currentUrl);
        } catch (e) {
          console.warn('Could not capture return URL before logout', e);
        }
      } else {
        this.logoutService.clearReturnUrl();
        this.broadcastChannelService.notifyLogoutInitiated(reason, null);
      }
    } else if (reason !== 'manual') {
      this.logoutService.setReturnUrl(currentUrl);
    } else {
      this.logoutService.clearReturnUrl();
    }

    this.clearInterval();
    this.#loginInProgress = false;
    this.#refreshInFlight$ = null;
    this.clearAzureIdToken();
    this.logoutService.logout(reason);
  }

  startMonitoring() {
    this.checkInterval = setInterval(() => {
      this.checkActivity();
    }, 1000);
  }

  private checkActivity() {
    if (!this.getAccessToken()) {
      return;
    }

    const lastActivity = parseInt(localStorage.getItem('lastActivity') || '0', 10);
    const currentTime = new Date().getTime();
    if (lastActivity === 0 || currentTime - lastActivity > INACTIVITY_TIMEOUT_MS) {
      this.logout('inactive');
    }
  }
}
